import type { BetLoopKambiShareCoupon } from '@core/Areas/BetLoop/types'
import type { KambiHistoryCoupon } from '@mobi/api-types/kambi'
import { getKambiCouponBetType } from './getKambiCouponBetType'

/** Sort Multi coupon outcomes/couponRows/events by event start */
export function rebuildKambiHistoryCoupon<T extends Coupon>(historyCoupon: T): T {
  const { events, outcomes, couponRows } = historyCoupon

  const betType = getKambiCouponBetType(historyCoupon)
  if (betType !== 'multi') {
    return historyCoupon
  }

  // ======
  // EVENTS
  // ======
  const orderedEvents = events.slice().sort((a, b) => {
    return Date.parse(a.eventStartDate) - Date.parse(b.eventStartDate)
  })

  const eventStartTimeById = new Map<number, number>(
    orderedEvents.map(event => [event.eventId, new Date(event.eventStartDate).getTime()])
  )

  // ========
  // OUTCOMES
  // ========
  const outcomeByOutcomeId = new Map(outcomes.map(outcome => [outcome.outcomeId, outcome]))

  const outcomesOrderedByEventTime = [...outcomes].sort((a, b) => {
    const eventTimeA = eventStartTimeById.get(a.eventId) ?? Number.POSITIVE_INFINITY
    const eventTimeB = eventStartTimeById.get(b.eventId) ?? Number.POSITIVE_INFINITY

    return eventTimeA - eventTimeB
  })

  // ===========
  // COUPON ROWS
  // ===========
  const couponRowsWithOutcomeId = couponRows.map((row, idx) => {
    const outcomeId = getCouponRowOutcomeId(row)
    const outcome = outcomeId !== undefined ? outcomeByOutcomeId.get(outcomeId) : undefined

    const startTime = outcome ? eventStartTimeById.get(outcome.eventId) : Number.POSITIVE_INFINITY

    return {
      row,
      idx,
      outcomeId,
      outcome,
      startTime,
    }
  })

  couponRowsWithOutcomeId.sort((a, b) => {
    const aValue = a.startTime ?? Number.POSITIVE_INFINITY
    const bValue = b.startTime ?? Number.POSITIVE_INFINITY
    return aValue - bValue
  })

  const orderedCouponRows = couponRowsWithOutcomeId.map(row => row.row)

  return {
    ...historyCoupon,
    outcomes: outcomesOrderedByEventTime,
    couponRows: orderedCouponRows,
    events: orderedEvents,
  }
}

// =============
// Local Helpers
// =============

const getCouponRowOutcomeId = (row: KambiHistoryCoupon['couponRows'][number]) => {
  const group = row.outcomeGroup?.groups?.[0]
  if (row.selectionType === 'BET_BUILDER' && group?.outcomeIds?.length) {
    return group.outcomeIds[0]
  }
  return row.outcomeId
}

// ===========
// Local Types
// ===========

type Coupon = KambiHistoryCoupon | BetLoopKambiShareCoupon
